import React, { useState, useRef, useEffect } from 'react';
import { 
  Container, 
  Paper, 
  Typography, 
  Box, 
  TextField, 
  Button, 
  Grid, 
  Tab, 
  Tabs,
  Card, 
  CardMedia, 
  CardContent, 
  CardActions, 
  IconButton, 
  Snackbar, 
  Alert,
  CircularProgress, 
  Divider, 
  Chip, 
  Select, 
  MenuItem,
  Badge,
  AppBar,
  Toolbar,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  LinearProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ImageIcon from '@mui/icons-material/Image';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';
import WarningIcon from '@mui/icons-material/Warning';
import TimerIcon from '@mui/icons-material/Timer';
import { searchPatternByCode, searchPatternByImage } from '../services/publicPatternService';
import { getSystemConfig } from '../services/configService';

// 多语言支持
const translations = {
  'zh': {
    title: '样板搜索',
    subtitle: '免登录搜索样板，快速便捷',
    codeSearch: '按代码搜索',
    imageSearch: '按图片搜索',
    searchPlaceholder: '输入样板代码...',
    searchButton: '搜索',
    uploadImage: '上传图片',
    dragDrop: '拖放图片到此处或点击上传',
    searchResults: '搜索结果',
    noResults: '未找到匹配的样板',
    viewDetails: '查看详情',
    copyCode: '复制代码',
    category: '分类',
    name: '名称',
    code: '代码',
    createdAt: '创建时间',
    language: '语言',
    contactUs: '联系我们',
    limitWarning: '搜索次数有限，请合理使用',
    searchesLeft: '剩余搜索次数',
    limitReached: '已达到搜索限制',
    limitReachedMessage: '您已达到搜索限制，请稍后再试或联系我们获取更多访问权限。',
    close: '关闭',
    error: '错误',
    invalidImage: '请选择有效的图片文件',
    contactInfo: '联系信息',
    tryAgain: '请在{0}分钟后重试',
    limitInfo: '每分钟限制{0}次搜索',
    unknownError: '搜索时发生未知错误',
    waiting: '处理中...'
  },
  'en': {
    title: 'Pattern Search',
    subtitle: 'Search patterns without login, quick and convenient',
    codeSearch: 'Search by Code',
    imageSearch: 'Search by Image',
    searchPlaceholder: 'Enter pattern code...',
    searchButton: 'Search',
    uploadImage: 'Upload Image',
    dragDrop: 'Drag and drop image here or click to upload',
    searchResults: 'Search Results',
    noResults: 'No matching patterns found',
    viewDetails: 'View Details',
    copyCode: 'Copy Code',
    category: 'Category',
    name: 'Name',
    code: 'Code',
    createdAt: 'Created At',
    language: 'Language',
    contactUs: 'Contact Us',
    limitWarning: 'Limited searches available, please use wisely',
    searchesLeft: 'Searches Left',
    limitReached: 'Search Limit Reached',
    limitReachedMessage: 'You have reached the search limit. Please try again later or contact us for more access.',
    close: 'Close',
    error: 'Error',
    invalidImage: 'Please select a valid image file',
    contactInfo: 'Contact Information',
    tryAgain: 'Please try again in {0} minutes',
    limitInfo: 'Limited to {0} searches per minute',
    unknownError: 'Unknown error occurred during search',
    waiting: 'Processing...'
  }
};

// 自定义样式
const Input = styled('input')({
  display: 'none',
});

const ImagePreview = styled(Box)(({ theme }) => ({
  border: `1px dashed ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  height: '250px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  backgroundColor: theme.palette.background.default,
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const LimitBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: (props) => 
      props.count <= 1 ? theme.palette.error.main : 
      props.count <= 2 ? theme.palette.warning.main : 
      theme.palette.success.main,
    color: theme.palette.common.white,
    fontWeight: 'bold',
    minWidth: '22px',
    height: '22px',
    padding: '0 6px'
  },
}));

const formatString = (str, ...args) => {
  return str.replace(/{(\d+)}/g, (match, number) => {
    return typeof args[number] !== 'undefined' ? args[number] : match;
  });
};

const PublicPatternSearch = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // 状态管理
  const [language, setLanguage] = useState('zh');
  const [tabValue, setTabValue] = useState(0);
  const [codeSearch, setCodeSearch] = useState('');
  const [imageData, setImageData] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [searchLimit, setSearchLimit] = useState(5); // 默认限制
  const [searchesLeft, setSearchesLeft] = useState(5); // 默认剩余次数
  const [limitReachedDialog, setLimitReachedDialog] = useState(false);
  const [contactInfo, setContactInfo] = useState({ phone: '', email: '', address: '' });
  const [configLoaded, setConfigLoaded] = useState(false);
  
  const fileInputRef = useRef(null);
  const t = translations[language];
  
  // 加载系统配置
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const config = await getSystemConfig(['searchLimit', 'contactPhone', 'contactEmail', 'contactAddress']);
        
        if (config.searchLimit) {
          setSearchLimit(parseInt(config.searchLimit));
          setSearchesLeft(parseInt(config.searchLimit));
        }
        
        setContactInfo({
          phone: config.contactPhone || '',
          email: config.contactEmail || '',
          address: config.contactAddress || ''
        });
        
        setConfigLoaded(true);
      } catch (error) {
        console.error('Failed to load system config:', error);
        // 使用默认值继续
        setConfigLoaded(true);
      }
    };
    
    loadConfig();
  }, []);
  
  // 处理标签切换
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // 重置搜索结果
    setSearchResults([]);
  };

  // 处理语言切换
  const handleLanguageChange = (event) => {
    setLanguage(event.target.value);
  };
  
  // 处理代码输入变更
  const handleCodeInputChange = (e) => {
    setCodeSearch(e.target.value);
  };
  
  // 处理文件选择
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImageData(reader.result);
          setPreviewImage(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        showSnackbar(t.invalidImage, 'error');
      }
    }
  };
  
  // 处理粘贴事件
  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const blob = items[i].getAsFile();
        const reader = new FileReader();
        reader.onloadend = () => {
          setImageData(reader.result);
          setPreviewImage(reader.result);
        };
        reader.readAsDataURL(blob);
        break;
      }
    }
  };
  
  // 处理图片区域点击
  const handleImageAreaClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  // 处理图片拖放
  const handleDragOver = (e) => {
    e.preventDefault();
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImageData(reader.result);
          setPreviewImage(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        showSnackbar(t.invalidImage, 'error');
      }
    }
  };
  
  // 显示消息提示
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };
  
  // 关闭消息提示
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  // 执行搜索
  const handleSearch = async () => {
    if (searchesLeft <= 0) {
      setLimitReachedDialog(true);
      return;
    }
    
    setLoading(true);
    
    try {
      let results;
      
      if (tabValue === 0) {
        // 代码搜索
        if (!codeSearch.trim()) {
          showSnackbar(t.searchPlaceholder, 'warning');
          setLoading(false);
          return;
        }
        
        results = await searchPatternByCode(codeSearch.trim());
      } else {
        // 图片搜索
        if (!imageData) {
          showSnackbar(t.uploadImage, 'warning');
          setLoading(false);
          return;
        }
        
        results = await searchPatternByImage(imageData);
      }
      
      // 处理后端返回的数据格式
      if (results.success && results.data) {
        // 将后端返回的数据转换为前端期望的格式
        const formattedResults = results.data.map(pattern => ({
          id: pattern.id,
          code: pattern.code,
          name: pattern.name,
          imageUrl: pattern.imageUrl || pattern.image_url
        }));
        setSearchResults(formattedResults);
        
        // 如果有_meta信息，更新搜索次数
        if (results._meta && typeof results._meta.remaining === 'number') {
          setSearchesLeft(results._meta.remaining);
        } else {
          // 更新剩余搜索次数
          setSearchesLeft(prevCount => Math.max(0, prevCount - 1));
        }
      } else {
        // 如果没有成功返回数据，显示空结果
        setSearchResults([]);
        showSnackbar(results.message || t.noResults, 'info');
      }
    } catch (error) {
      console.error('Search error:', error);
      
      if (error.response?.status === 429) {
        // 达到限制
        setLimitReachedDialog(true);
      } else {
        showSnackbar(error.response?.data?.message || t.unknownError, 'error');
      }
    } finally {
      setLoading(false);
    }
  };
  
  // 复制代码到剪贴板
  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code)
      .then(() => {
        showSnackbar(`${t.code} ${code} ${t.copyCode}`, 'success');
      })
      .catch((error) => {
        console.error('Failed to copy code:', error);
      });
  };
  
  // 查看样板详情
  const handleViewDetails = (patternId) => {
    // 实际实现可能需要打开模态框或导航到详情页面
    window.open(`/pattern-details/${patternId}`, '_blank');
  };
  
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {!configLoaded ? (
        <Box sx={{ width: '100%', my: 4 }}>
          <LinearProgress />
        </Box>
      ) : (
        <>
          <AppBar position="static" color="default" elevation={0} sx={{ mb: 3, borderRadius: 1 }}>
            <Toolbar>
              <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                {t.title}
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                  <TimerIcon sx={{ mr: 1, color: theme.palette.warning.main }} />
                  <LimitBadge 
                    count={searchesLeft}
                    badgeContent={searchesLeft} 
                    color="primary"
                  >
                    <Typography variant="body2">
                      {t.searchesLeft}
                    </Typography>
                  </LimitBadge>
                </Box>
                
                <Select
                  value={language}
                  onChange={handleLanguageChange}
                  size="small"
                  startAdornment={<LanguageIcon sx={{ mr: 1 }} />}
                >
                  <MenuItem value="zh">中文</MenuItem>
                  <MenuItem value="en">English</MenuItem>
                </Select>
              </Box>
            </Toolbar>
          </AppBar>
          
          <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
            <Typography variant="h4" gutterBottom align="center">
              {t.title}
            </Typography>
            <Typography variant="subtitle1" align="center" color="textSecondary" gutterBottom>
              {t.subtitle}
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
              <Chip 
                icon={<WarningIcon />} 
                label={formatString(t.limitInfo, searchLimit)}
                color="default" 
                variant="outlined" 
              />
            </Box>
            
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              centered
              indicatorColor="primary"
              textColor="primary"
              sx={{ mb: 3 }}
            >
              <Tab label={t.codeSearch} icon={<ContentCopyIcon />} />
              <Tab label={t.imageSearch} icon={<ImageIcon />} />
            </Tabs>
            
            {tabValue === 0 ? (
              <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder={t.searchPlaceholder}
                  value={codeSearch}
                  onChange={handleCodeInputChange}
                  disabled={loading}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SearchIcon />}
                  onClick={handleSearch}
                  disabled={loading || !codeSearch.trim()}
                  sx={{ minWidth: isMobile ? '100%' : '120px' }}
                >
                  {loading ? t.waiting : t.searchButton}
                </Button>
              </Box>
            ) : (
              <Box>
                <ImagePreview
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onClick={handleImageAreaClick}
                  onPaste={handlePaste}
                  tabIndex={0}
                >
                  <Input
                    accept="image/*"
                    id="upload-image"
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                  />
                  
                  {previewImage ? (
                    <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <img
                        src={previewImage}
                        alt="Preview"
                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                      />
                      <IconButton
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          backgroundColor: 'rgba(0,0,0,0.5)',
                          color: 'white',
                          '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' }
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setImageData(null);
                          setPreviewImage(null);
                        }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </div>
                  ) : (
                    <>
                      <FileUploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                      <Typography variant="body1" align="center" color="textSecondary">
                        {t.dragDrop}
                      </Typography>
                    </>
                  )}
                </ImagePreview>
                
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SearchIcon />}
                    onClick={handleSearch}
                    disabled={loading || !imageData}
                    sx={{ minWidth: '150px' }}
                  >
                    {loading ? t.waiting : t.searchButton}
                  </Button>
                </Box>
              </Box>
            )}
          </Paper>
          
          {/* 搜索结果区域 */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : searchResults.length > 0 ? (
            <>
              <Typography variant="h5" gutterBottom>
                {t.searchResults}
              </Typography>
              
              <Grid container spacing={3}>
                {searchResults.map((pattern) => (
                  <Grid item xs={12} sm={6} md={4} key={pattern.id}>
                    <Card elevation={2}>
                      <CardMedia
                        component="img"
                        height="200"
                        image={pattern.imageUrl || 'https://placeholder.pics/svg/300x200/DEDEDE/555555/No%20Image'}
                        alt={pattern.name}
                      />
                      
                      <CardContent>
                        <Typography variant="h6" noWrap title={pattern.name}>
                          {pattern.name}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                          <Typography variant="body2" color="textSecondary" sx={{ mr: 1 }}>
                            {t.code}:
                          </Typography>
                          <Chip label={pattern.code} size="small" />
                        </Box>
                      </CardContent>
                      
                      <CardActions>
                        <Button
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={() => handleViewDetails(pattern.id)}
                        >
                          {t.viewDetails}
                        </Button>
                        
                        <IconButton
                          size="small"
                          onClick={() => handleCopyCode(pattern.code)}
                          title={t.copyCode}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </>
          ) : (
            searchResults.length === 0 && (tabValue === 0 && codeSearch || tabValue === 1 && previewImage) && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="textSecondary">
                  {t.noResults}
                </Typography>
              </Box>
            )
          )}
          
          {/* 联系信息区域 */}
          <Paper elevation={1} sx={{ mt: 4, p: 3, backgroundColor: 'rgba(0,0,0,0.02)' }}>
            <Typography variant="h6" gutterBottom>
              {t.contactInfo}
            </Typography>
            
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              {contactInfo.phone && (
                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PhoneIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                    <Typography variant="body1">{contactInfo.phone}</Typography>
                  </Box>
                </Grid>
              )}
              
              {contactInfo.email && (
                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <EmailIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                    <Typography variant="body1">{contactInfo.email}</Typography>
                  </Box>
                </Grid>
              )}
              
              {contactInfo.address && (
                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <LanguageIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                    <Typography variant="body1">{contactInfo.address}</Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Paper>
        </>
      )}
      
      {/* 限制达到对话框 */}
      <Dialog
        open={limitReachedDialog}
        onClose={() => setLimitReachedDialog(false)}
      >
        <DialogTitle sx={{ backgroundColor: theme.palette.error.main, color: 'white' }}>
          {t.limitReached}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <DialogContentText>
            {t.limitReachedMessage}
          </DialogContentText>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, p: 2, backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: 1 }}>
            <TimerIcon sx={{ mr: 1, color: theme.palette.warning.main }} />
            <Typography variant="body2">
              {formatString(t.tryAgain, '1')}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLimitReachedDialog(false)}>
            {t.close}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* 消息提示 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PublicPatternSearch;
